import { React, useRef, useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { googleSignInErrorMessage } from '../../contexts/AuthContext/googleSignInError';

import {
	Card,
	Form,
	Container,
	Button,
	Alert,
} from 'react-bootstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
	resolvePostLoginPath,
	stashPostLoginPath,
	takeStashedPostLoginPath,
} from './postLoginPath';

// This page has an email/password form on screen, so that is the useful advice
// when Google fails for a reason we do not recognise.
const GOOGLE_FALLBACK = 'Failed to sign in with Google. Please try again.';

export default function PortalLogin() {
	const emailRef = useRef();
	const passwordRef = useRef();

	const { login, signInWithGoogle, currentUser, userRole, logout, redirectError, clearRedirectError } =
		useAuth();
	const [error, setError] = useState('');
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const location = useLocation();

	// Where RoleBasedRoute said this visitor was headed (an emailed link to a
	// portal page, say). Stashed at once because a redirect-based Google sign-in
	// leaves the page and loses router state — see postLoginPath.js.
	const from = location.state?.from || null;
	useEffect(() => {
		if (from) stashPostLoginPath(from);
	}, [from]);

	// Signed in but no users doc: accounts are enrollment-gated (§3g) and
	// created only by the emailed claim link — a bare sign-in stays unlinked.
	// Without this banner the page would just sit there after a Google sign-in.
	const unlinked = Boolean(currentUser && !userRole);

	// A successful sign-in normally navigates away (loading never needs a
	// reset) — but an unlinked sign-in stays on this page, so un-stick the
	// buttons from their "Logging in…" state.
	useEffect(() => {
		if (unlinked) setLoading(false);
	}, [unlinked]);

	// A FAILED REDIRECT SIGN-IN LANDS BACK HERE WITH NOTHING TO SHOW FOR IT.
	// On mobile and in the installed PWA, signInWithGoogle leaves the page
	// entirely, so the catch above never runs — the failure arrives later, as a
	// rejected getRedirectResult inside AuthContext. Without this the parent
	// returns to a page that looks exactly as it did before they tapped.
	// Cleared on consumption so it cannot resurface on a later visit.
	useEffect(() => {
		if (!redirectError) return;
		const message = googleSignInErrorMessage(redirectError, GOOGLE_FALLBACK);
		if (message) setError(message);
		setLoading(false);
		clearRedirectError();
	}, [redirectError, clearRedirectError]);

	// Redirect once user is authenticated and role is loaded
	useEffect(() => {
		if (currentUser && userRole) {
			console.log('🔄 Auto-redirect triggered - User:', currentUser.email, 'Role:', userRole);
			// Land where the visitor was headed when they were sent here, if that
			// is a portal page for this role; otherwise the role's home. Staff
			// home is /portal (E3b front-door flip: owners get the hub, a
			// single-space teacher is routed on by PortalSpaceEntry); a family's
			// is /portal/customer. The stash covers a redirect-based Google
			// sign-in, where router state did not survive the round trip.
			const dest = resolvePostLoginPath(userRole, from || takeStashedPostLoginPath());
			navigate(dest, { replace: true });
		}
	}, [currentUser, userRole, navigate, from]);

	async function handleSubmit(e) {
		e.preventDefault();

		try {
			setError('');
			setLoading(true);
			await login(
				emailRef.current.value,
				passwordRef.current.value
			);
			// The useEffect hook will handle the redirect once auth state updates
		} catch (error) {
			console.error('Login error:', error);
			setLoading(false);

			// With email enumeration protection ON (enabled 2026-08-02), Firebase
			// collapses 'user-not-found' and 'wrong-password' into one code —
			// deliberately, so a failed login cannot reveal whether the address has
			// an account. BOTH spellings are matched on purpose: this SDK version
			// emits 'invalid-login-credentials' (verified in the browser 2026-08-02),
			// while newer Firebase SDKs emit 'invalid-credential'. Matching only one
			// means an SDK bump silently drops this branch back to the vague
			// fallback. The two legacy codes stay for the same reason.
			if (
				error.code === 'auth/invalid-login-credentials' ||
				error.code === 'auth/invalid-credential' ||
				error.code === 'auth/user-not-found' ||
				error.code === 'auth/wrong-password'
			) {
				setError('Invalid email or password.');
			} else if (error.code === 'auth/too-many-requests') {
				setError('Too many failed attempts. Please try again later.');
			} else {
				setError('Failed to log in. Please check your credentials.');
			}
		}
	}

	async function handleGoogleSignIn() {
		try {
			setError('');
			setLoading(true);
			await signInWithGoogle();
			// The useEffect hook will handle the redirect once auth state updates
		} catch (error) {
			console.error('Google sign-in error:', error);
			setLoading(false);
			// Same mapper as the redirect path below — the two used to map
			// errors separately, which is how one of them ended up showing
			// nothing at all. A null message means the user closed the popup.
			const message = googleSignInErrorMessage(error, GOOGLE_FALLBACK);
			if (message) setError(message);
		}
	}

	return (
		<>
			<Container className='justify-content-center py-5' fluid>
				<Card
					className='mx-auto bg-transparent border-1 p-2 shadow-sm'
					style={{ maxWidth: '450px' }}>
					<Card.Header className='bg-transparent border-0 text-center'>
						<h2 className='mb-0' style={{ color: '#384971' }}>
							Portal Login
						</h2>
						<p className='text-muted small mt-2'>
							Sign in to access your portal
						</p>
					</Card.Header>
					<Card.Body>
						{unlinked && (
							<Alert variant='info'>
								<strong>
									You're signed in as {currentUser.email},
									but that sign-in isn't linked to a portal
									account yet.
								</strong>{' '}
								Portal accounts are set up through an emailed
								invite after your family is placed for lessons
								— check your inbox for a "Set up your account"
								email, or contact us for a fresh link.
								<div className='d-grid mt-3'>
									<Button
										variant='outline-secondary'
										size='sm'
										onClick={() => logout()}>
										Sign out
									</Button>
								</div>
							</Alert>
						)}
						{error && <Alert variant='danger'>{error}</Alert>}
						<Form onSubmit={handleSubmit}>
							<Form.Group className='mb-3'>
								<Form.Label>Email Address</Form.Label>
								<Form.Control
									type='email'
									placeholder='Enter your email'
									ref={emailRef}
									required
									autoComplete='email'
								/>
							</Form.Group>
							<Form.Group className='mb-2'>
								<Form.Label>Password</Form.Label>
								<Form.Control
									type='password'
									placeholder='Enter your password'
									ref={passwordRef}
									required
									autoComplete='current-password'
								/>
							</Form.Group>
							<div className='text-end mb-4'>
								<Link
									to='/portal/forgot-password'
									className='text-muted small'>
									Forgot Password?
								</Link>
							</div>
							<div className='d-grid gap-2'>
								<Button
									disabled={loading}
									className='shadow-none'
									variant='primary'
									type='submit'
									size='lg'>
									{loading ? 'Logging in...' : 'Log In'}
								</Button>
							</div>
						</Form>

						<div className='text-center mb-3 mt-3'>
							<span className='text-muted small'>OR</span>
						</div>

						<div className='d-grid gap-2'>
							<Button
								onClick={handleGoogleSignIn}
								disabled={loading}
								className='shadow-none'
								variant='outline-secondary'
								size='lg'>
								<i className='fab fa-google me-2'></i>
								{loading ? 'Signing in...' : 'Sign in with Google'}
							</Button>
						</div>
					</Card.Body>
					<Card.Footer className='bg-transparent border-0 text-center'>
						<p className='mb-2'>
							Don't have a portal account?{' '}
							<Link to='/portal/signup'>How to get access</Link>
						</p>
						{/* Install Portal App promo removed for summer (#25) — PWA
							install is deferred to fall. */}
						{/* This used to link to `/login`. That route now redirects
							HERE, so the link would loop back to the page the reader is
							already standing on. A confused parent still needs a way
							forward, so it points at a person instead of a door. */}
						<p className='text-muted small mb-0'>
							Looking for our old system? It closes August 31 —{' '}
							<Link to='/contact'>get in touch</Link> and we'll help.
						</p>
					</Card.Footer>
				</Card>
			</Container>
		</>
	);
}
